import React, { type ReactElement, useEffect, useRef, useState } from 'react';

import classnames from 'classnames';
import { decode } from 'entities';

import safeNumber from '../../utils/safeNumber';
import { Button } from '../Button';
import { type IconColor, type IconName } from '../Icon';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Spinner } from '../Spinner';
import { H4 } from '../Typography';

import styles from './Snackbar.module.scss';

const STANDARD_DISMISS_AFTER = 6000;
const STANDARD_MESSAGE_LENGTH = 120;

export enum SnackbarType {
    Custom = 'custom',
    Error = 'error',
    Information = 'information',
    Success = 'success',
    Warning = 'warning'
}

export type SnackbarProps = {
    button?: {
        action: () => void;
        dismissViaAction?: boolean;
        text: string;
    };
    /*
     * An optional secondary button to be used alongside the primary button
     */
    secondaryButton?: {
        action: () => void;
        dismissViaAction?: boolean;
        text: string;
    };
    /*
     * If true, the actions will be displayed below the message
     */
    compact?: boolean;
    dismissAfter?: number;
    dismissAllowed?: boolean;
    header?: string;
    id?: string;
    /*
     * An optional icon name to use when the type is 'custom'
     */
    customIconName?: IconName;
    loading?: boolean;
    message: string;
    onDismiss: () => void;
    hideCloseButton?: boolean;
    type: SnackbarType;
    /*
     * An optional width for the Snackbar, has to be at least 380px
     */
    width?: number;
};

const DISMISS_ANIMATION_DURATION = 300;

export const Snackbar = ({
    button,
    secondaryButton,
    compact = false,
    dismissAfter,
    dismissAllowed = true,
    header,
    id,
    customIconName,
    loading,
    message,
    onDismiss,
    hideCloseButton,
    type,
    width
}: SnackbarProps): ReactElement => {
    const [isDismissed, setIsDismissed] = useState(false);
    const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const withActions = Boolean(button);
    const isCompact = compact || (width !== undefined && width <= 500 && withActions);

    if (width !== undefined && width < 380) {
        throw new Error('Snackbar width must be at least 380px');
    }

    const iconNameMap: Record<SnackbarType, IconName> = {
        [SnackbarType.Error]: 's-icon-circle-exclamation',
        [SnackbarType.Success]: 's-icon-circle-check',
        [SnackbarType.Warning]: 's-icon-circle-exclamation',
        [SnackbarType.Information]: 's-icon-circle-info',
        [SnackbarType.Custom]: customIconName ?? 's-icon-placeholder'
    };
    const iconColorMap: Record<SnackbarType, IconColor> = {
        [SnackbarType.Error]: 'danger',
        [SnackbarType.Success]: 'success',
        [SnackbarType.Warning]: 'warning',
        [SnackbarType.Information]: 'information',
        [SnackbarType.Custom]: 'base'
    };
    const iconName = iconNameMap[type];
    const iconColor = iconColorMap[type];

    const notificationClassNames = (className: string, notificationType: SnackbarType) =>
        classnames(className, {
            [styles.danger]: notificationType === SnackbarType.Error,
            [styles.success]: notificationType === SnackbarType.Success,
            [styles.warning]: notificationType === SnackbarType.Warning,
            [styles.information]: notificationType === SnackbarType.Information,
            [styles.base]: notificationType === SnackbarType.Custom
        });

    const calculateDismissTime = (messageLength: number) => {
        if (messageLength <= STANDARD_MESSAGE_LENGTH) {
            return STANDARD_DISMISS_AFTER;
        }
        return (
            STANDARD_DISMISS_AFTER +
            Math.floor(((messageLength - STANDARD_MESSAGE_LENGTH) * 1000) / STANDARD_MESSAGE_LENGTH)
        );
    };

    const handleDismiss = () => {
        setIsDismissed(true);
        animationTimeoutRef.current = setTimeout(() => {
            onDismiss();
        }, DISMISS_ANIMATION_DURATION);
    };

    const handleButtonAction = () => {
        if (button?.dismissViaAction) {
            setIsDismissed(true);
            animationTimeoutRef.current = setTimeout(() => {
                button?.action();
            }, DISMISS_ANIMATION_DURATION);
        } else {
            button?.action();
        }
    };

    const handleSecondaryButtonAction = () => {
        if (secondaryButton?.dismissViaAction) {
            setIsDismissed(true);
            animationTimeoutRef.current = setTimeout(() => {
                secondaryButton?.action();
            }, DISMISS_ANIMATION_DURATION);
        } else {
            secondaryButton?.action();
        }
    };

    useEffect(() => {
        if (dismissAfter) {
            timeoutRef.current = setTimeout(() => handleDismiss(), dismissAfter);
        } else if (dismissAllowed) {
            timeoutRef.current = setTimeout(
                handleDismiss,
                calculateDismissTime(
                    header
                        ? header.length + safeNumber(message?.length)
                        : safeNumber(message?.length)
                )
            );
        }
        return () => {
            if (timeoutRef?.current) {
                clearTimeout(timeoutRef.current);
            }
            if (animationTimeoutRef?.current) {
                clearTimeout(animationTimeoutRef.current);
            }
        };
    }, [animationTimeoutRef, dismissAfter, dismissAllowed, header, message, timeoutRef]);

    return (
        <div
            className={classnames(notificationClassNames(styles.notification, type), {
                [styles.isDismissed]: isDismissed,
                [styles.compact]: isCompact
            })}
            id={id}
            style={{ width }}
        >
            <div className={styles.icon}>
                {loading ? (
                    <Spinner aria-label="spinner" size={24} />
                ) : (
                    <Icon name={iconName} color={iconColor} size="large" />
                )}
            </div>
            <div className={styles.wrapper}>
                <div className={styles.content}>
                    {header && <H4 className={styles.header}>{header}</H4>}
                    <div className={styles.message}>{decode(message ?? '')}</div>
                </div>
                {button && (
                    <div className={styles.actions}>
                        {secondaryButton && (
                            <Button
                                size="medium"
                                className={styles.notificationButton}
                                onClick={handleSecondaryButtonAction}
                                type="ghost"
                            >
                                {secondaryButton.text}
                            </Button>
                        )}
                        <Button
                            size="medium"
                            className={styles.notificationButton}
                            onClick={handleButtonAction}
                            type="outlined"
                        >
                            {button.text}
                        </Button>
                    </div>
                )}
            </div>
            {!hideCloseButton && (
                <IconButton
                    aria-label="close notification"
                    className={styles.notificationCloseIcon}
                    onClick={handleDismiss}
                    size="medium"
                    type="ghost"
                    color="neutral"
                    icon={<Icon name="s-icon-xmark" size="medium" />}
                />
            )}
        </div>
    );
};

Snackbar.displayName = 'Snackbar';
